import React, { useState, useEffect } from 'react';
import { api } from '../api/client';

const EMPTY_SEGMENT = { sort_order: 0, prize_type: 'points', prize_value: '', rarity: 'common', weight: 1, label: '', coupon_grams: '', active: true };

export default function WheelConfigPanel() {
  const [segments, setSegments] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_SEGMENT);
  const [msg, setMsg] = useState(null);

  const fetchSegments = () => {
    api.get('/admin/wheel-segments').then(setSegments).catch(() => {});
  };

  useEffect(fetchSegments, []);

  const handleSave = async () => {
    setMsg(null);
    try {
      if (editing) {
        await api.put(`/admin/wheel-segments/${editing}`, form);
      } else {
        await api.post('/admin/wheel-segments', form);
      }
      setEditing(null);
      setForm(EMPTY_SEGMENT);
      fetchSegments();
      setMsg({ type: 'success', text: editing ? 'Updated' : 'Created' });
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this segment?')) return;
    try {
      await api.delete(`/admin/wheel-segments/${id}`);
      fetchSegments();
    } catch {
      // ignore
    }
  };

  const startEdit = (seg) => {
    setEditing(seg.id);
    setForm({
      sort_order: seg.sort_order,
      prize_type: seg.prize_type,
      prize_value: seg.prize_value,
      rarity: seg.rarity,
      weight: seg.weight,
      label: seg.label,
      coupon_grams: seg.coupon_grams || '',
      active: seg.active,
    });
  };

  return (
    <div className="card">
      <h3 style={{ marginBottom: 12 }}>Wheel Segments</h3>

      {msg && (
        <div style={{ padding: '8px 12px', borderRadius: 'var(--radius-sm)', marginBottom: 12, fontSize: 13,
          background: msg.type === 'success' ? 'rgba(72,187,120,0.1)' : 'rgba(252,129,129,0.1)',
          color: msg.type === 'success' ? 'var(--success)' : 'var(--danger)',
          border: `1px solid ${msg.type === 'success' ? 'var(--success)' : 'var(--danger)'}`,
        }}>
          {msg.text}
        </div>
      )}

      <div style={{ overflowX: 'auto', marginBottom: 16 }}>
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Label</th>
              <th>Type</th>
              <th>Value</th>
              <th>Rarity</th>
              <th>Weight</th>
              <th>Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {segments.map(seg => (
              <tr key={seg.id}>
                <td>{seg.sort_order}</td>
                <td>{seg.label}</td>
                <td>{seg.prize_type}</td>
                <td>{seg.prize_value}</td>
                <td><span className={`badge badge-${seg.rarity}`}>{seg.rarity}</span></td>
                <td>{parseFloat(seg.weight)}</td>
                <td>{seg.active ? 'Yes' : 'No'}</td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button className="btn-primary btn-small" onClick={() => startEdit(seg)}>Edit</button>
                    <button className="btn-danger btn-small" onClick={() => handleDelete(seg.id)}>Del</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', padding: 16 }}>
        <h4 style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
          {editing ? 'Edit Segment' : 'Add Segment'}
        </h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8, marginBottom: 12 }}>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>Sort Order</label>
            <input type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>Label</label>
            <input type="text" value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>Prize Type</label>
            <select value={form.prize_type} onChange={e => setForm(f => ({ ...f, prize_type: e.target.value }))}>
              <option value="points">Points</option>
              <option value="coupon">Coupon</option>
              <option value="none">None</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>Prize Value</label>
            <input type="text" value={form.prize_value} onChange={e => setForm(f => ({ ...f, prize_value: e.target.value }))} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>Rarity</label>
            <select value={form.rarity} onChange={e => setForm(f => ({ ...f, rarity: e.target.value }))}>
              <option value="common">Common</option>
              <option value="uncommon">Uncommon</option>
              <option value="rare">Rare</option>
              <option value="legendary">Legendary</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>Weight</label>
            <input type="number" step="0.001" value={form.weight} onChange={e => setForm(f => ({ ...f, weight: parseFloat(e.target.value) || 0 }))} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>Coupon Grams</label>
            <input type="text" value={form.coupon_grams} onChange={e => setForm(f => ({ ...f, coupon_grams: e.target.value }))} placeholder="e.g. 1g" />
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>Active</label>
            <select value={form.active ? 'true' : 'false'} onChange={e => setForm(f => ({ ...f, active: e.target.value === 'true' }))}>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-primary btn-small" onClick={handleSave}>{editing ? 'Update' : 'Add'}</button>
          {editing && <button className="btn-secondary btn-small" onClick={() => { setEditing(null); setForm(EMPTY_SEGMENT); }}>Cancel</button>}
        </div>
      </div>
    </div>
  );
}
